/**
 * RaceResults Component
 *
 * Final results display with podium and full rankings.
 */

import { useNavigate } from 'react-router-dom';
import type { RaceRoom, LeaderboardEntry } from '@/types/race';
import { getAppById } from '@/config/apps';
import styles from './Race.module.css';

interface RaceResultsProps {
  room: RaceRoom | null;
  leaderboard: LeaderboardEntry[];
}

const APP_ICONS: Record<string, string> = {
  addition: '+',
  multiplication: '\u00d7',
  subtraction: '-',
  division: '\u00f7',
};

export function RaceResults({ leaderboard }: RaceResultsProps) {
  const navigate = useNavigate();
  const podium = leaderboard.slice(0, 3);

  // Reorder for podium display: 2nd, 1st, 3rd
  const podiumDisplay = [
    podium[1], // 2nd place (left)
    podium[0], // 1st place (center)
    podium[2], // 3rd place (right)
  ].filter(Boolean);

  const formatTime = (ms: number | null) => {
    if (!ms) return '-';
    const seconds = ms / 1000;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <div className={styles.resultsContainer}>
      <h1 className={styles.resultsTitle}>Race Complete!</h1>

      {/* Podium */}
      {podium.length > 0 && (
        <div className={styles.podium}>
          {podiumDisplay.map((entry, displayIndex) => {
            if (!entry) return <div key={displayIndex} className={styles.podiumPlaceholder} />;

            const app = getAppById(entry.participant.app_name);
            const actualRank = entry.rank;
            const podiumHeight = actualRank === 1 ? 'tall' : actualRank === 2 ? 'medium' : 'short';

            return (
              <div
                key={entry.participant.id}
                className={`${styles.podiumPlace} ${styles[`podium${podiumHeight}`]}`}
              >
                <div className={styles.podiumAvatar}>
                  <span className={styles.podiumRank}>{actualRank}</span>
                </div>
                <div className={styles.podiumName}>{entry.participant.username}</div>
                <div
                  className={styles.podiumApp}
                  style={{ '--app-color': app?.color || '#666' } as React.CSSProperties}
                >
                  {APP_ICONS[entry.participant.app_name]} {app?.shortName}
                </div>
                <div className={styles.podiumStats}>
                  <span>{entry.correctCount} / {entry.cardsCompleted} correct</span>
                  <span>{Math.round(entry.accuracy)}% accuracy</span>
                </div>
                <div className={styles.podiumPedestal} />
              </div>
            );
          })}
        </div>
      )}

      {/* Full Results Table */}
      <div className={styles.resultsTable}>
        <h2 className={styles.resultsTableTitle}>Full Results</h2>

        <div className={styles.resultsTableHeader}>
          <span className={styles.colRank}>Rank</span>
          <span className={styles.colName}>Name</span>
          <span className={styles.colApp}>App</span>
          <span className={styles.colScore}>Score</span>
          <span className={styles.colAccuracy}>Accuracy</span>
          <span className={styles.colTime}>Time</span>
        </div>

        {leaderboard.map((entry) => {
          const app = getAppById(entry.participant.app_name);

          return (
            <div key={entry.participant.id} className={styles.resultsTableRow}>
              <span className={styles.colRank}>
                <span className={`${styles.rankBadge} ${entry.rank <= 3 ? styles[`rank${entry.rank}`] : ''}`}>
                  {entry.rank}
                </span>
              </span>
              <span className={styles.colName}>{entry.participant.username}</span>
              <span className={styles.colApp} style={{ '--app-color': app?.color || '#666' } as React.CSSProperties}>
                {APP_ICONS[entry.participant.app_name]} {app?.shortName || entry.participant.app_name}
              </span>
              <span className={styles.colScore}>
                {entry.correctCount} / {entry.cardsCompleted}
              </span>
              <span className={styles.colAccuracy}>{Math.round(entry.accuracy)}%</span>
              <span className={styles.colTime}>{formatTime(entry.totalTime)}</span>
            </div>
          );
        })}

        {leaderboard.length === 0 && (
          <div className={styles.resultsEmpty}>No results to display</div>
        )}
      </div>

      {/* Actions */}
      <div className={styles.resultsActions}>
        <button onClick={() => navigate('/race')} className={styles.primaryButton}>
          Back to Dashboard
        </button>
      </div>
    </div>
  );
}
